import { useEffect, useState } from 'react';
import { Button } from '../../../ui/button';
import Entity from '../../entity/Entity';
import { Input } from '@/common-utils/components/ui/input';
import './TablePagination.scss';
import PropTypes from 'prop-types';
import ICONS from '@/common-utils/enums/icons';
import useDeviceSizeStore from '@/app/store/deviceSize';

/**
 * @param root0
 * @param root0.table
 * @param root0.pagination
 * @param root0.pageUpdate
 * @param root0.dataFetchingIsLoading
 * @param root0.resetPagination
 * @param root0.setResetPagination
 * @param root0.currentPageCount
 * @param root0.selectionMode
 * @returns {JSX} TablePagination
 */
function TablePagination({ table, pagination, pageUpdate, dataFetchingIsLoading, resetPagination, setResetPagination, currentPageCount, selectionMode = false }) {

    // Constants
    const pageCount = table.getPageCount();

    // State
    const [currentPageNumber, setCurrentPageNumber] = useState(pagination.pageIndex + 1);

    //Store
    const isLargeDevice = useDeviceSizeStore(state => state.isLargeDevice);

    // Hooks
    useEffect(() => {
        if (resetPagination) {
            setCurrentPageNumber(pagination.pageIndex + 1);
            setResetPagination(false);
        }
    }, [resetPagination]);

    // Render
    return (
        <div id='table-pagination' className='flex items-center justify-end space-x-2 pt-4 pb-1'>
            {
                selectionMode ?
                    <Entity
                        className={'flex-1 text-sm text-muted-foreground'}
                        entity={'table.pagination.rowSelected'}
                        data={{ rowSelected: Object.keys(table.getState().rowSelection).length, rowCount: currentPageCount.rowCount }}
                    />
                    :
                    <div className='flex-1 text-sm text-muted-foreground'>
                        <Entity entity={'table.results'} data={{ results: currentPageCount.rowCount }} />
                    </div>
            }
            <div className='space-x-2 flex items-center'>
                <Button
                    variant='secondary'
                    size='sm'
                    entity={isLargeDevice ? 'button.previous' : undefined}
                    icon={!isLargeDevice ? ICONS.CHEVRON_LEFT : undefined}
                    onClick={() => {
                        table.previousPage();
                        setCurrentPageNumber(currentPageNumber - 1);
                        pageUpdate(false);
                    }}
                    disabled={currentPageNumber === 1 || currentPageNumber === '' || currentPageNumber === '0' || dataFetchingIsLoading}
                />
                <Input
                    type={'text'}
                    className={'text border border-secondary-foreground max-h-9'}
                    style={{ width: `${String(currentPageNumber || '').length + 4}ch` }}
                    data-cy='page-jump-input'
                    disabled={dataFetchingIsLoading}
                    onFocus={() => {
                        // Removes previous number before user's typing
                        setCurrentPageNumber('');
                    }}
                    onBlur={() => {
                        // When user's lost focus on this input :

                        // If input has its value like '' or '0', set pageIndex to 0 and input value to 1
                        if (currentPageNumber === '' || currentPageNumber === '0') {
                            pageUpdate(true, 0);
                            setCurrentPageNumber(1);
                        }
                        // If input has its value beyond the number of pages, set pageIndex to the last page index number and input value to the last page number
                        else if (currentPageNumber > pageCount) {
                            pageUpdate(true, (pageCount - 1) * pagination.pageSize);
                            setCurrentPageNumber(pageCount);
                        }
                        // Else set pageIndex to the current page index number and input value to the current page number
                        else {
                            pageUpdate(true, (currentPageNumber - 1) * pagination.pageSize);
                        }
                    }}
                    value={currentPageNumber}
                    onChange={(event) => {
                        const regex = /\D+/gm;
                        const cleanedValue = event.target.value.replace(regex, '');
                        setCurrentPageNumber(cleanedValue);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 13 || event.which === 13 || event.keyCode === 13) {
                            // Linked to the onBlur effect above
                            event.currentTarget.blur();
                        }
                    }}
                />
                <Entity
                    entity={'table.pagination.pageCount'}
                    data={{ pageCount: currentPageCount.pageCount }}
                />
                <Button
                    variant='secondary'
                    size='sm'
                    entity={isLargeDevice ? 'button.next' : undefined}
                    icon={!isLargeDevice ? ICONS.MORE_INFO : undefined}
                    onClick={() => {
                        table.nextPage();
                        setCurrentPageNumber(parseInt(currentPageNumber) + 1);
                        pageUpdate(true);
                    }}
                    disabled={pageCount <= 1 || currentPageNumber === pageCount || currentPageNumber === '' || dataFetchingIsLoading}
                />
            </div>
        </div>
    );
}
TablePagination.propTypes = {
    table: PropTypes.object.isRequired,
    pagination: PropTypes.object.isRequired,
    pageUpdate: PropTypes.func.isRequired,
    dataFetchingIsLoading: PropTypes.bool.isRequired,
    resetPagination: PropTypes.bool.isRequired,
    setResetPagination: PropTypes.func.isRequired,
    currentPageCount: PropTypes.object.isRequired,
    selectionMode: PropTypes.bool
};

export default TablePagination;